// https://www.figma.com/design/4nTOFTtmTmvTmhW9tfnXO7/Ankr-Design-System-2.0?node-id=9384-62751&m=dev
// MUI v5 color tokens mapped 1:1 to Ankr UI Palette tokens

import { createTheme } from '@mui/material/styles';

/**
 * Palette augmentation to include border/surface groups and extra text fields.
 */
declare module '@mui/material/styles' {
  interface Palette {
    border: {
      primary: string;
      secondary: string;
      tertiary: string;
      inverted: string;
      immutable: string;
      brand: string;
      brandHover: string;
      disabled: string;
      error: string;
      warning: string;
      success: string;
      focused: string;
      black: string;
    };
    surface: {
      primary: string;
      primaryInverted: string;
      secondary: string;
      tertiary: string;
      hover: string;
      neutral: string;
      inverted: string;
      overlay: string;

      brand: string;
      brandHover: string;
      brandLight: string;

      error: string;
      errorHover: string;
      warning: string;
      warningHover: string;
      success: string;
      successHover: string;

      purple: string;
      teal: string;
      indigo: string;
      indigoHover: string;
      magenta: string;
      magentaHover: string;

      infoLight: string;
      successLight: string;
      errorLight: string;
      purpleLight: string;
      tealLight: string;
      indigoLight: string;
      magentaLight: string;
      warningLight: string;
    };
    dividerSecondary: string;
  }

  interface TypeText {
    primaryHover: string;
    primaryImmutable: string;
    brand: string;
    positive: string;
    negative: string;
    warning: string;
    purple: string;
    immutable: string;
  }

  interface PaletteOptions {
    border?: Partial<Palette['border']>;
    surface?: Partial<Palette['surface']>;
    dividerSecondary?: string;
  }
}

// Extra text props on top of MUI defaults
type ExtendedTextOptions = {
  primary?: string;
  secondary?: string;
  disabled?: string;
  primaryHover?: string;
  primaryImmutable?: string;
  brand?: string;
  positive?: string;
  negative?: string;
  warning?: string;
  purple?: string;
  immutable?: string;
};

const lightPalette = {
  mode: 'light' as const,

  // MUI core is taken from baseTheme
  primary: {
    main: '#356DF3',
    dark: '#2A5BD1',
    light: '#E1E9FD',
    contrastText: '#ffffff',
  },

  secondary: {
    main: '#FFFFFF',
    dark: '#E7EBF3',
    light: '#E7EBF3',
    contrastText: '#82899A',
  },

  info: {
    main: '#FFFFFF',
    dark: '#E7EBF3',
    light: '#E7EBF3',
    contrastText: '#356DF3',
  },

  error: {
    main: '#D22C54',
    dark: '#B32547',
    light: '#F8DFE5',
    contrastText: '#FFFFFF',
  },

  warning: {
    main: '#EEA941',
    dark: '#D89A3B',
    light: '#FCF2E2',
    contrastText: '#1F2226',
  },

  success: {
    main: '#3AC090',
    dark: '#31A37A',
    light: '#E1F6EE',
    contrastText: '#FFFFFF',
  },

  highlight: {
    main: '#F4E9D0',
    dark: '#FFA030',
  },

  background: {
    default: '#F2F5FA', // surface-primary.light
    paper: '#FFFFFF', // surface-primary-inverted.light
  },

  grey: {
    100: '#E7EBF3',
    200: '#DFE3EB',
    300: '#BFC6D0',
    400: '#A7AFC0',
    500: '#9AA1B0',
    600: '#82899A',
    700: '#585E6B',
    800: '#2E343C',
    900: '#1F2226',
  },

  yellow: {
    main: '#FFB800',
    dark: '#D9AD00',
    light: '#FFF7D9',
  },

  teal: {
    main: '#3FDDDD',
    dark: '#36BCBC',
    light: '#E2FAFA',
  },

  indigo: {
    main: '#5856D6',
    dark: '#4B49B6',
    light: '#E6E6F9',
  },

  purple: {
    main: '#AF52DE',
    dark: '#9546BD',
    light: '#F3E5FA',
  },

  link: {
    main: '#356DF3',
  },

  text: {
    primary: '#1F2226', // text-primary.light
    secondary: '#62899A', // text-secondary.light
    disabled: '#A7AFC0', // text-disabled.light
    primaryHover: '#2E343C', // text-primary-hover.light
    primaryImmutable: '#1F2226', // text-primary-immutable.light
    brand: '#356DF3', // text-brand.light
    positive: '#3AC090', // text-positive.light
    negative: '#D32547', // text-negative.light
    warning: '#EEA841', // text-warning.light
    purple: '#AF52DE', // text-purple.light
    immutable: '#FFFFFF', // text-immutable.light
  } as ExtendedTextOptions,

  divider: '#E7EBF3', // div-primary.light (fixed from E7E8F3)
  dividerSecondary: '#DFE3EB', // div-secondary.light (fixed from DFE3E8)

  border: {
    primary: '#62899A', // border-primary.light (grey 600)
    secondary: '#F2F5FA', // border-secondary.light (background light)
    tertiary: '#F2F5FA', // border-tertiary.light (same as secondary)
    inverted: '#FFFFFF', // border-inverted.light
    immutable: '#FFFFFF', // border-immutable.light
    brand: '#356DF3', // border-brand.light
    brandHover: '#356DF3', // border-brand-hover.light
    disabled: '#A7AFC0', // border-disabled.light
    error: '#A7AFC0', // border-error.light (disabled grey for error state)
    warning: '#EEA841', // border-warning.light
    success: '#3AC090', // border-success.light
    focused: '#356DF380', // border-focused.light (50% opacity of brand)
    black: '#1F2226', // border-black.light (true black color)
  },

  surface: {
    primary: '#F2F5FA', // surface-primary.light
    primaryInverted: '#FFFFFF', // surface-primary-inverted.light
    secondary: '#62899A', // surface-secondary.light
    tertiary: '#E7EBF3', // surface-tertiary.light
    hover: '#DFE3EB', // surface-hover.light
    neutral: '#DFE5E8', // surface-neutral.light
    inverted: '#1F2226', // surface-inverted.light
    overlay: '#2E343C', // surface-overlay.light

    brand: '#356DF3', // surface-brand.light
    brandHover: '#356DF3', // surface-brand-hover.light
    brandLight: '#E1E8FD', // surface-brand-light.light

    error: '#D32547', // surface-error.light
    errorHover: '#C2694E', // surface-error-hover.light
    warning: '#EEA841', // surface-warning.light
    warningHover: '#D38A38', // surface-warning-hover.light
    success: '#3AC090', // surface-success.light
    successHover: '#31A37A', // surface-success-hover.light

    purple: '#AF52DE', // surface-purple.light
    teal: '#31A37A', // surface-teal.light
    indigo: '#356DF6', // surface-indigo.light
    indigoHover: '#4B4968', // surface-indigo-hover.light
    magenta: '#EE538B', // surface-magenta.light
    magentaHover: '#B61C5F', // surface-magenta-hover.light

    infoLight: '#E6E8F9', // surface-info-light.light
    successLight: '#E1F8E8', // surface-success-light.light
    errorLight: '#FFE5E8', // surface-error-light.light
    purpleLight: '#F3E5FA', // surface-purple-light.light
    tealLight: '#E2F9FA', // surface-teal-light.light
    indigoLight: '#E6E8F9', // surface-indigo-light.light
    magentaLight: '#FFD4E8', // surface-magenta-light.light
    warningLight: '#FCEF22', // surface-warning-light.light
  },
};

const darkPalette = {
  mode: 'dark' as const,

  // MUI core is taken from baseTheme
  primary: {
    main: '#5A87F2',
    dark: '#2A64EE',
    light: '#E1E9FD',
    contrastText: '#ffffff',
  },

  secondary: {
    main: '#FFFFFF',
    dark: '#42464F',
    light: '#42464F',
    contrastText: '#82899A',
  },

  info: {
    main: '#FFFFFF',
    dark: '#42464F',
    light: '#42464F',
    contrastText: '#5A87F2',
  },

  error: {
    main: '#EC5478',
    dark: '#C8294E',
    light: '#350C15',
    contrastText: '#FFFFFF',
  },

  warning: {
    main: '#F0B358',
    dark: '#DCA451',
    light: '#554D42',
    contrastText: '#1F2226',
  },

  success: {
    main: '#4BC99B',
    dark: '#37B788',
    light: '#0F3224',
    contrastText: '#FFFFFF',
  },

  highlight: {
    main: '#F4E9D0',
    dark: '#FFA030',
  },

  grey: {
    100: '#42464F',
    200: '#535863',
    300: '#BFC6D0',
    400: '#A7AFC0',
    500: '#9AA1B0',
    600: '#82899A',
    700: '#585E6B',
    800: '#9AA1B0',
    900: '#FFFFFF',
  },

  link: {
    main: '#5A87F2',
  },

  yellow: {
    main: '#FFD11A',
    dark: '#F3C200',
    light: '#3F3200',
  },

  teal: {
    main: '#54E1E1',
    dark: '#43C9C9',
    light: '#093333',
  },

  indigo: {
    main: '#6C6BDB',
    dark: '#5D5BBD',
    light: '#0B0B2E',
  },

  purple: {
    main: '#B967E2',
    dark: '#A059C4',
    light: '#23092F',
  },

  common: {
    black: '#000000',
    white: '#ffffff',
  },

  text: {
    primary: '#fff', // text-primary.dark
    secondary: '#82899A', // text-secondary.dark
    disabled: '#A7AFC0', // text-disabled.dark
    primaryHover: '#9AA1B0', // text-primary-hover.dark
    primaryImmutable: '#1F2226', // text-primary-immutable.dark
    brand: '#5A87F2', // text-brand.dark
    positive: '#48C398', // text-positive.dark
    negative: '#C2694E', // text-negative.dark
    warning: '#F0B358', // text-warning.dark
    purple: '#B96FE2', // text-purple.dark
    immutable: '#FFFFFF', // text-immutable.dark
  } as ExtendedTextOptions,

  background: {
    default: '#1F2226', // surface-primary.dark
    paper: '#2E343C', // surface-primary-inverted.dark
  },

  divider: '#42464F', // div-primary.dark
  dividerSecondary: '#535863', // div-secondary.dark

  border: {
    primary: '#62899A', // border-primary.dark
    secondary: '#1F2226', // border-secondary.dark
    tertiary: '#1F2226', // border-tertiary.dark
    inverted: '#2E343C', // border-inverted.dark
    immutable: '#FFFFFF', // border-immutable.dark
    brand: '#5AB7F2', // border-brand.dark
    brandHover: '#5AB7F2', // border-brand-hover.dark
    disabled: '#A7AFC0', // border-disabled.dark
    error: '#A7AFC0', // border-error.dark
    warning: '#F0B358', // border-warning.dark
    success: '#48C398', // border-success.dark
    focused: '#5AB7F280', // border-focused.dark (50% opacity)
    black: '#FFFFFF', // border-black.dark
  },

  surface: {
    primary: '#1F2226', // surface-primary.dark
    primaryInverted: '#2E343C', // surface-primary-inverted.dark
    secondary: '#62899A', // surface-secondary.dark
    tertiary: '#42464F', // surface-tertiary.dark
    hover: '#535863', // surface-hover.dark
    neutral: '#513863', // surface-neutral.dark
    inverted: '#FFFFFF', // surface-inverted.dark
    overlay: '#9AA1B0', // surface-overlay.dark

    brand: '#5AB7F2', // surface-brand.dark
    brandHover: '#5AB7F2', // surface-brand-hover.dark
    brandLight: '#E1E8FD', // surface-brand-light.dark

    error: '#D32547', // surface-error.dark
    errorHover: '#C2694E', // surface-error-hover.dark
    warning: '#F0B358', // surface-warning.dark
    warningHover: '#DCA451', // surface-warning-hover.dark
    success: '#48C398', // surface-success.dark
    successHover: '#378798', // surface-success-hover.dark

    purple: '#B96FE2', // surface-purple.dark
    teal: '#54E181', // surface-teal.dark
    indigo: '#6CABD8', // surface-indigo.dark
    indigoHover: '#5D58BD', // surface-indigo-hover.dark
    magenta: '#D12771', // surface-magenta.dark
    magentaHover: '#9F1853', // surface-magenta-hover.dark

    infoLight: '#D6D8CE', // surface-info-light.dark
    successLight: '#0f3224', // surface-success-light.dark (placeholder)
    errorLight: '#D93333', // surface-error-light.dark (placeholder)
    purpleLight: '#7A529C', // surface-purple-light.dark (placeholder)
    tealLight: '#3B664D', // surface-teal-light.dark (placeholder)
    indigoLight: '#3E4A5E', // surface-indigo-light.dark (placeholder)
    magentaLight: '#DF1853', // surface-magenta-light.dark (placeholder)
    warningLight: '#C4A600', // surface-warning-light.dark (placeholder)
  },
};

// ---- Theme factory -----------------------------------------------------------

export const buildTheme = (mode: 'light' | 'dark' = 'light') =>
  createTheme({
    palette: mode === 'light' ? lightPalette : darkPalette,
  });

export const lightTheme = buildTheme('light');
export const darkTheme = buildTheme('dark');
